/**
 * Download PDF for the print preview — turns the previewed print page into a
 * PDF file in the browser (no server, no Firebase reads).
 *
 * It works from the SAME page the preview shows (same template, paper size,
 * letterhead and every Print Settings choice), so the PDF matches the print:
 *  - paper size A4 / A5 portrait / A5 landscape, as set in Print Settings;
 *  - the printer's page margins on EVERY page (not only the first);
 *  - letterhead header / footer / watermark repeated on every page;
 *  - pages break between table rows / sections, not through a line of text;
 *  - the "Powered by AmalERP" line at the bottom of every page (when the print
 *    has it — see printBranding.js).
 *
 * Pictures from another website that refuses to share them (e.g. a logo stored
 * without download permission) cannot be put in a PDF by any browser; they are
 * left out and `missingImages` tells the caller so it can suggest
 * Print → "Save as PDF" for a 100% exact copy.
 */
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { drawPdfBrandLine } from './printBranding';

export const PAPER_MM = {
  A4: { w: 210, h: 297 },
  A5P: { w: 148, h: 210 },
  A5L: { w: 210, h: 148 },
};
export const PX_PER_MM = 96 / 25.4;

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(r.error);
  r.readAsDataURL(blob);
});

const waitForImage = (img) => new Promise((resolve) => {
  if (img.complete && img.naturalWidth > 0) { resolve(); return; }
  const done = () => resolve();
  img.addEventListener('load', done, { once: true });
  img.addEventListener('error', done, { once: true });
  setTimeout(done, 8000);
});

/** Wait for every picture on the page (logo, QR, letterhead), max 8 s. */
export const waitForImages = (doc) =>
  Promise.all(Array.from(doc.images || []).map(waitForImage));

/**
 * Turn every picture on the page into an embedded copy so the PDF can include
 * it. Returns how many pictures could not be embedded.
 */
const embedImages = async (doc) => {
  let missing = 0;
  const imgs = Array.from(doc.images || []);
  await Promise.all(imgs.map(async (img) => {
    const src = img.getAttribute('src') || '';
    if (!src || src.startsWith('data:') || src.startsWith('blob:')) return;
    try {
      const res = await fetch(img.src, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const dataUrl = await blobToDataUrl(await res.blob());
      img.src = dataUrl;
      await waitForImage(img);
    } catch (e) {
      missing += 1;
    }
  }));
  return missing;
};

const loadImage = (src) => new Promise((resolve) => {
  if (!src) { resolve(null); return; }
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

// Where a page may end: under a table row or a block (never through a line).
const breakPoints = (doc, topOffset) => {
  const sel = 'tr, table, p, h1, h2, h3, h4, li, .header, .info-grid, .totals, .notes-section, '
    + '.signatures, .footer-text, .print-page > *, .body-content > *, body > div > *';
  const set = new Set();
  doc.querySelectorAll(sel).forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.height > 0) set.add(Math.round(r.bottom - topOffset));
  });
  return Array.from(set).sort((a, b) => a - b);
};

/**
 * Build the PDF. `doc` = the preview's page (iframe document). Returns
 * { blob, missingImages, pages }.
 */
export async function buildPdfFromPreview(doc, { paperSize = 'A4', letterhead = null, brand = null } = {}) {
  const paper = PAPER_MM[paperSize] || PAPER_MM.A4;
  const pageWpx = paper.w * PX_PER_MM;
  const pageHpx = paper.h * PX_PER_MM;
  const body = doc.body;
  const win = doc.defaultView;

  await waitForImages(doc);
  const missingImages = await embedImages(doc);

  // The page margin the printer uses = the side padding the preview gives the
  // page; it is applied to the top and bottom of EVERY PDF page.
  const cs = win.getComputedStyle(body);
  const sideMarginPx = parseFloat(cs.paddingLeft) || 0;

  // Letterhead: header / footer heights decide each page's top / bottom space
  // (15 mm when only one of them is uploaded — same as the print).
  let topPx = sideMarginPx;
  let bottomPx = sideMarginPx;
  let lhImgs = null;
  if (letterhead) {
    topPx = (letterhead.headerUrl ? letterhead.headerHeightMm : 15) * PX_PER_MM;
    bottomPx = (letterhead.footerUrl ? letterhead.footerHeightMm : 15) * PX_PER_MM;
    const pick = (cls) => { const el = doc.querySelector(cls); return el ? el.src : null; };
    const [header, footer, watermark] = await Promise.all([
      loadImage(pick('.lh-header')), loadImage(pick('.lh-footer')), loadImage(pick('.lh-watermark')),
    ]);
    lhImgs = { header, footer, watermark };
  }

  // "Powered by AmalERP" line: drawn on every page, in the bottom space (or,
  // with a letterhead footer picture, in a 6 mm band kept free above it).
  // 'halves' (two copies on a sheet) carries it inside each copy already.
  let brandYmm = null;
  if (brand === 'page') {
    brandYmm = paper.h - Math.max(4, Math.min(12, bottomPx / PX_PER_MM - 3));
  } else if (brand === 'letterhead') {
    if (letterhead && letterhead.footerUrl) {
      brandYmm = paper.h - (Number(letterhead.footerHeightMm) || 0) - 2;
      bottomPx += 6 * PX_PER_MM;
    } else {
      brandYmm = paper.h - 7;
    }
  }

  // Capture the content only: no top/bottom page space (added per page below)
  // and no letterhead pictures (drawn on every page below).
  const saved = {
    pt: body.style.getPropertyValue('padding-top'), ptP: body.style.getPropertyPriority('padding-top'),
    pb: body.style.getPropertyValue('padding-bottom'), pbP: body.style.getPropertyPriority('padding-bottom'),
  };
  const hidden = [];
  try {
    body.style.setProperty('padding-top', '0px', 'important');
    body.style.setProperty('padding-bottom', '0px', 'important');
    if (letterhead) {
      doc.querySelectorAll('.lh-header, .lh-footer, .lh-watermark').forEach((el) => {
        hidden.push([el, el.style.display]);
        el.style.display = 'none';
      });
    }
    if (brandYmm !== null) {
      doc.querySelectorAll('.amal-print-brand, .amal-print-brand-lh').forEach((el) => {
        hidden.push([el, el.style.display]);
        el.style.setProperty('display', 'none', 'important');
      });
    }

    const root = doc.documentElement;
    const topOffset = root.getBoundingClientRect().top;
    // The page's own height (not the preview sheet, which is at least one page
    // tall) — so a short invoice is one PDF page, not one page plus a blank one.
    // Measured from every element, because a floated block (the totals box) does
    // not stretch the page body.
    let bottom = body.getBoundingClientRect().bottom;
    body.querySelectorAll('*').forEach((el) => {
      if (win.getComputedStyle(el).position === 'fixed') return;
      const r = el.getBoundingClientRect();
      if (r.height > 0 && r.bottom > bottom) bottom = r.bottom;
    });
    const contentHpx = Math.max(1, Math.ceil(bottom - topOffset + 2));
    const points = breakPoints(doc, topOffset);

    const scale = 2;
    const canvas = await html2canvas(root, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: letterhead && lhImgs && lhImgs.watermark ? null : '#ffffff',
      width: Math.ceil(pageWpx),
      height: Math.ceil(contentHpx),
      windowWidth: Math.ceil(pageWpx),
      windowHeight: Math.ceil(contentHpx),
      scrollX: 0,
      scrollY: 0,
    });

    const usable = Math.max(40, pageHpx - topPx - bottomPx);
    const pdf = new jsPDF({
      orientation: paper.w > paper.h ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [Math.min(paper.w, paper.h), Math.max(paper.w, paper.h)],
      compress: true,
    });
    const useJpeg = !(letterhead && lhImgs && lhImgs.watermark);

    // Watermark is drawn at its print opacity, once, then reused on every page.
    let watermarkData = null;
    let wmW = 0;
    let wmH = 0;
    if (lhImgs && lhImgs.watermark) {
      const wm = lhImgs.watermark;
      wmW = paper.w * (Number(letterhead.watermarkWidthPct) || 60) / 100;
      wmH = wmW * (wm.naturalHeight / wm.naturalWidth || 1);
      if (wmH > paper.h * 0.7) { wmW *= (paper.h * 0.7) / wmH; wmH = paper.h * 0.7; }
      const c = document.createElement('canvas');
      c.width = wm.naturalWidth; c.height = wm.naturalHeight;
      const ctx = c.getContext('2d');
      ctx.globalAlpha = Math.min(1, Math.max(0, Number(letterhead.watermarkOpacity ?? 0.08)));
      ctx.drawImage(wm, 0, 0);
      try { watermarkData = c.toDataURL('image/png'); } catch (e) { watermarkData = null; }
    }
    const imgData = (img) => {
      if (!img) return null;
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        return c.toDataURL('image/png');
      } catch (e) { return null; }
    };
    const headerData = lhImgs ? imgData(lhImgs.header) : null;
    const footerData = lhImgs ? imgData(lhImgs.footer) : null;

    let y0 = 0;
    let pages = 0;
    while (y0 < contentHpx - 1 && pages < 200) {
      let y1 = Math.min(contentHpx, y0 + usable);
      if (y1 < contentHpx) {
        // Prefer ending under a row/section in the last 40% of the page.
        const minEnd = y0 + usable * 0.6;
        for (let i = points.length - 1; i >= 0; i -= 1) {
          if (points[i] <= y1 && points[i] > minEnd) { y1 = points[i]; break; }
        }
      }
      if (y1 <= y0) y1 = Math.min(contentHpx, y0 + usable);

      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = Math.max(1, Math.round((y1 - y0) * scale));
      const sctx = slice.getContext('2d');
      if (useJpeg) { sctx.fillStyle = '#ffffff'; sctx.fillRect(0, 0, slice.width, slice.height); }
      sctx.drawImage(canvas, 0, Math.round(y0 * scale), canvas.width, slice.height, 0, 0, slice.width, slice.height);

      if (pages > 0) pdf.addPage();
      if (watermarkData) pdf.addImage(watermarkData, 'PNG', (paper.w - wmW) / 2, (paper.h - wmH) / 2, wmW, wmH);
      pdf.addImage(
        useJpeg ? slice.toDataURL('image/jpeg', 0.92) : slice.toDataURL('image/png'),
        useJpeg ? 'JPEG' : 'PNG',
        0, topPx / PX_PER_MM, paper.w, (y1 - y0) / PX_PER_MM,
      );
      if (headerData) pdf.addImage(headerData, 'PNG', 0, 0, paper.w, letterhead.headerHeightMm);
      if (footerData) pdf.addImage(footerData, 'PNG', 0, paper.h - letterhead.footerHeightMm, paper.w, letterhead.footerHeightMm);
      if (brandYmm !== null) drawPdfBrandLine(pdf, paper.w, brandYmm);

      pages += 1;
      y0 = y1;
    }

    return { blob: pdf.output('blob'), missingImages, pages };
  } finally {
    // Put the preview back exactly as it was.
    if (saved.pt) body.style.setProperty('padding-top', saved.pt, saved.ptP); else body.style.removeProperty('padding-top');
    if (saved.pb) body.style.setProperty('padding-bottom', saved.pb, saved.pbP); else body.style.removeProperty('padding-bottom');
    hidden.forEach(([el, d]) => { el.style.display = d; });
  }
}

/** Save a PDF blob with a clean file name, e.g. "SI-2026-00012.pdf". */
export function downloadBlob(blob, name) {
  const safe = String(name || 'document').replace(/[\\/:*?"<>|]+/g, '-').trim() || 'document';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safe.toLowerCase().endsWith('.pdf') ? safe : `${safe}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
